import type { ReactNode } from 'react';
import { ApiRefusal, ApiUnreachable } from '@gatecheckpro/api-client';
import { Notice } from '@gatecheckpro/design-system';

/**
 * Turns a thrown failure into something a person can act on.
 *
 * Three things must stay distinguishable, because the right next move differs
 * for each: the network did not reach us, the server refused deliberately, or
 * something broke. Collapsing them into "Something went wrong" is how a product
 * teaches its users that its error messages are not worth reading.
 */
const REMEDY: Readonly<Record<string, string>> = {
  AUTHZ_MFA_REQUIRED:
    'Every operations capability needs a second factor. Enrol one, then sign in again.',
  AUTHZ_CAPABILITY_DENIED:
    'This capability has not been granted to you. There is no operator role that implies it.',
  ORGANIZATION_VERIFICATION_STATE_INVALID:
    'This organization has already moved on. Reload to see its current state.',
  RESOURCE_NOT_FOUND: 'No such record, or it is outside what this capability can see.',
  AUTH_RATE_LIMITED: 'Too many attempts. Wait a minute and try again.',
};

export function describeFailure(error: unknown): {
  tone: 'critical' | 'caution';
  title: string;
  message: string;
  detail: string | null;
} {
  if (error instanceof ApiUnreachable) {
    return {
      tone: 'caution',
      title: 'Could not reach GateCheckPro',
      message: 'Nothing was changed. Check the connection and try again.',
      detail: null,
    };
  }
  if (error instanceof ApiRefusal) {
    return {
      tone: error.status >= 500 ? 'critical' : 'caution',
      title: error.status >= 500 ? 'GateCheckPro could not complete that' : 'That was refused',
      message: error.message,
      // The request id is what support correlates against the audit trail.
      detail:
        REMEDY[error.code] ?? (error.requestId === '' ? null : `Reference ${error.requestId}`),
    };
  }
  return {
    tone: 'critical',
    title: 'Something went wrong',
    message: 'The workspace hit an unexpected problem. Reload and try again.',
    detail: null,
  };
}

export function FailureNotice({ error }: { readonly error: unknown }): ReactNode {
  if (error === null || error === undefined) return null;
  const described = describeFailure(error);
  return (
    <Notice tone={described.tone} title={described.title}>
      {described.message}
      {described.detail !== null && (
        <div className="gc-muted" style={{ marginTop: '0.25rem' }}>
          {described.detail}
        </div>
      )}
    </Notice>
  );
}
